import parser from 'any-date-parser'
import dayjs from 'dayjs'
import { DateFormat } from '../types'

// Excel Date Number to Date
export function excelDateToDate(excelDate: number, format = 'DD/MM/YYYY') {
  // Convert Excel serial number to a date
  const date = dayjs('1899-12-30').add(excelDate, 'day')
  const output = date.format(format)
  console.log(output)
  return output
}

// Date to Excel Date Number
export function dateToExcelDate(date: string, format = 'DD/MM/YYYY') {
  // Convert date to Excel serial number
  const excelDate = dayjs(date, format).diff(dayjs('1899-12-30'), 'day')
  console.log(excelDate)
  return excelDate
}

/**
 * MM/DD/YYYY or MM-DD-YYYY (e.g., 12/31/2023 for the US)
 * DD/MM/YYYY or DD-MM-YYYY (e.g., 31/12/2023 for the UK/AUS)
 * YYYY-MM-DD (ISO format, e.g., 2023-12-31)
 * Month Day, Year (e.g., December 31, 2023)
 * Day Month Year (e.g., 31 December 2023)
 * DD/Mon/YYYY (e.g., 01/Jan/2023)
 * Mon/DD/YYYY (e.g., Jan/01/2023)
 * @param text
 * @returns dates
 */
export const extractDateFromText = (text: string): string[] => {
  const dateRegex =
    /\b(?:(\d{2})[/-](\d{2})[/-](\d{4})|(\d{4})[/-](\d{2})[/-](\d{2})|([A-Za-z]+) (\d{1,2}),? (\d{4})|(\d{1,2}) ([A-Za-z]+) (\d{4})|(\d{2})[/]([A-Za-z]{3,9})[/](\d{4})|([A-Za-z]{3,9})[/](\d{2})[/](\d{4}))\b/g

  return text.match(dateRegex) ?? []
}

/**
 * Extract date from text
 * if date is in (year month day) format
 * e.g. 2016-09-24, 2016-9-24
 * @param text
 * @returns
 */
export const extractYearMonthDayFromText = (text: string) => {
  const re = /\b(\d{4})[-](0?[1-9]|1[0-2])[-](0?[1-9]|[12][0-9]|3[01])\b/g
  return text.match(re) ?? []
}

export const extractDayMonthNameYearFromText = (text: string) => {
  const re =
    /\b(0?[1-9]|[12][0-9]|3[01])[-\s]?(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[-\s]?(20\d{2}|\d{2})\b/g
  return text.match(re) ?? []
}

export const extractMonthNameDayYearFromText = (text: string) => {
  const re =
    /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[\s](0?[1-9]|[12][0-9]|3[01])[, ]\s?(20\d{2})\b/g
  return text.match(re) ?? []
}

export const extractMonthDayYear = (text: string) => {
  const re =
    /\b(0?[1-9]|1[0-2])[-/](0?[1-9]|[12][0-9]|3[01])[-/](\d{4}|\d{2})\b/g
  return text.match(re) ?? []
}

export const extractDayMonthYear = (text: string) => {
  const re =
    /\b(0?[1-9]|[12][0-9]|3[01])[-/.](0?[1-9]|1[0-2])[-/.](\d{4}|\d{2})\b/g
  return text.match(re) ?? []
}

export const extractMonthNameDay = (text: string) => {
  const re =
    /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[-\s/]?(0?[1-9]|[12][0-9]|3[01])\b/gi

  const matches = [...text.matchAll(re)]
  return matches.map(([, month, day]) => `${month} ${day}`) as RegExpMatchArray
}

export const extractDayMonthName = (text: string) => {
  const re =
    /\b(0?[1-9]|[12][0-9]|3[01])[-\s/]?(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/gi

  const matches = [...text.matchAll(re)]
  return matches.map(([, day, month]) => `${day} ${month}`) as RegExpMatchArray
}

export const extractMonthDay = (text: string) => {
  const re = /\b(0?[1-9]|1[0-2])[-/](0?[1-9]|[12][0-9]|3[01])\b/g
  return text.match(re) ?? []
}

export const extractDayMonth = (text: string) => {
  const re = /\b(0?[1-9]|[12][0-9]|3[01])[-/.](0?[1-9]|1[0-2])\b/g
  return text.match(re) ?? []
}

const getLocale = (format: DateFormat) => {
  switch (format) {
    case DateFormat.AU:
      return 'en-AU'
    case DateFormat.CA:
      return 'en-AU'
    case DateFormat.UK:
      return 'en-GB'
    default:
      return 'en-US'
  }
}

export const isValidDate = (
  date: string | undefined,
  locale: DateFormat
): {
  valid: boolean
  date: any
} => {
  if (!date) {
    return {
      valid: false,
      date: '',
    }
  }
  const localFormat = getLocale(locale)
  const parsed = parser.attempt(date, localFormat)
  if ('invalid' in parsed)
    return {
      valid: false,
      date: '',
    }
  return { valid: true, date: parsed }
}

export const extractAndParseDate = (
  text: string,
  local: DateFormat
): string | null => {
  let result = extractYearMonthDayFromText(text)
  let valid = isValidDate(result.at(0), local)
  if (valid.valid) {
    return dayjs(
      `${valid.date.year}-${valid.date.month}-${valid.date.day}`,
      'YYYY-M-D'
    ).format('YYYY-MM-DD')
  }

  result = extractDayMonthNameYearFromText(text)
  valid = isValidDate(result.at(0), local)
  if (valid.valid) {
    return dayjs(
      `${valid.date.year}-${valid.date.month}-${valid.date.day}`,
      'YYYY-M-D'
    ).format('YYYY-MM-DD')
  }

  result = extractMonthNameDayYearFromText(text)
  valid = isValidDate(result.at(0), local)
  if (valid.valid) {
    return dayjs(
      `${valid.date.year}-${valid.date.month}-${valid.date.day}`,
      'YYYY-M-D'
    ).format('YYYY-MM-DD')
  }

  result = extractMonthDayYear(text)
  valid = isValidDate(result.at(0), local)
  if (valid.valid) {
    valid = transformPossibleYearTo2000(valid)
    return dayjs(
      `${valid.date.year}-${valid.date.month}-${valid.date.day}`,
      'YYYY-M-D'
    ).format('YYYY-MM-DD')
  }

  result = extractDayMonthYear(text)
  valid = isValidDate(result.at(0), local)
  if (valid.valid) {
    valid = transformPossibleYearTo2000(valid)
    return dayjs(
      `${valid.date.year}-${valid.date.month}-${valid.date.day}`,
      'YYYY-M-D'
    ).format('YYYY-MM-DD')
  }

  result = extractMonthNameDay(text)
  valid = isValidDate(result.at(0), local)
  if (valid.valid) {
    return dayjs(`${valid.date.month}-${valid.date.day}`, 'M-D').format('MM-DD')
  }

  result = extractDayMonthName(text)
  valid = isValidDate(result.at(0), local)
  if (valid.valid) {
    return dayjs(`${valid.date.month}-${valid.date.day}`, 'M-D').format('MM-DD')
  }

  // result = extractMonthDay(text)
  // valid = isValidDate(result.at(0))
  // if (valid.valid) {
  //   return dayjs(`${valid.date.month}-${valid.date.day}`, 'M-D').format('MM-DD')
  // }

  // result = extractDayMonth(text)
  // valid = isValidDate(result.at(0))
  // if (valid.valid) {
  //   return dayjs(`${valid.date.month}-${valid.date.day}`, 'M-D').format('MM-DD')
  // }

  return null
}

const isValidYear = (date: string | null): boolean => {
  if (!date) return false
  const arr = date.split('-')
  if (arr.length === 1) return false
  if (arr.length === 2) {
    const month = arr[0]
    const day = arr[1]
    const m = parseInt(month)
    const d = parseInt(day)
    if (isNaN(m) || isNaN(d)) return false
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) return true
    return false
  }
  const year = arr[0]
  const y = parseInt(year)
  if (isNaN(y)) return false
  if (y >= 1900 && y <= 2100) return true
  return false
}

export const extractAndParseDateWithYearValidation = (
  str: string,
  local: DateFormat
): string | null => {
  const date = extractAndParseDate(str, local)
  if (isValidYear(date)) return date
  return null
}

export const convertNumberToExcelDate = (
  num: number,
  format: string = 'YYYY-MM-DD'
) => {
  if (num <= 0) return null

  const excelBaseDate = dayjs('1899-12-30')
  const date = excelBaseDate.add(num, 'day')
  return date.format(format)
}

/**
 * Checks if a string contains only date components (no additional text).
 * This function validates that the input string represents a date-only format
 * without any surrounding text like labels, prefixes, or suffixes.
 *
 * Supported date formats:
 * - Full dates: YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD, YYYY MM DD (e.g., "2025-01-01", "2025 01 01")
 * - Month/Day/Year: MM-DD-YYYY, DD-MM-YYYY with various separators (e.g., "01-15-2025", "15/01/2025")
 * - Short year: MM-DD-YY, DD-MM-YY with various separators (e.g., "01-15-25")
 * - Month/Day only: MM-DD, DD-MM, MM DD (e.g., "01-15", "15 01")
 * - Year/Month: YYYY-MM, YYYY MM (e.g., "2025-01", "2025 01")
 * - Month/Year: MM-YYYY, MM YYYY (e.g., "01-2025", "01 2025")
 * - Year only: YYYY (e.g., "2025")
 * - Month or Day only: MM, DD (e.g., "01", "15")
 *
 * Supported separators: hyphen (-), slash (/), dot (.), space ( )
 *
 * @param text - The string to validate
 * @returns true if the string contains only date components, false otherwise
 *
 * @example
 * ```typescript
 * isDateOnly("2025-01-01")     // true
 * isDateOnly("2025 01 01")     // true
 * isDateOnly("01/15/2025")     // true
 * isDateOnly("15.01")          // true
 * isDateOnly("2025")           // true
 * isDateOnly("Date: 2025-01-01") // false - contains additional text
 * isDateOnly("abc")            // false - not a date
 * isDateOnly("123 456 789")    // false - doesn't match date patterns
 * ```
 */
export const isDateOnly = (text: string): boolean => {
  const normalizedText = text.trim()

  // Check if text contains only date-related characters
  if (!/^[\d/\-. ]+$/.test(normalizedText)) return false

  // Must contain at least one digit
  if (!/\d/.test(normalizedText)) return false

  // Check common date patterns
  const datePatterns = [
    /^\d{4}[-/.\s]\d{1,2}[-/.\s]\d{1,2}$/, // YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD, YYYY MM DD
    /^\d{1,2}[-/.\s]\d{1,2}[-/.\s]\d{4}$/, // MM-DD-YYYY, DD-MM-YYYY variants with space
    /^\d{1,2}[-/.\s]\d{1,2}[-/.\s]\d{2}$/, // MM-DD-YY, DD-MM-YY variants with space
    /^\d{1,2}[-/.\s]\d{1,2}$/, // MM-DD, DD-MM, MM DD
    /^\d{4}[-/.\s]\d{1,2}$/, // YYYY-MM, YYYY MM
    /^\d{1,2}[-/.\s]\d{4}$/, // MM-YYYY, MM YYYY
    /^\d{4}$/, // YYYY only
    /^\d{1,2}$/, // MM or DD only
  ]

  return datePatterns.some((pattern) => pattern.test(normalizedText))
}

export const transformPossibleYearTo2000 = (valid: any): any => {
  if (valid.date.year === 0) {
    valid.date.year = 2000;
  }
  return valid;
}
